import { authService } from "../auth/authService";
import { env } from "../config/env";

type HttpMethod = "GET" | "POST";

interface ApiRequestOptions {
  method?: HttpMethod;
  body?: unknown;
  headers?: Record<string, string>;
}

export class ApiError extends Error {
  status: number;
  body: any;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

// Picks the readable message out of a backend response ({ message, errors: [...] } or ASP.NET validation errors)
function messageFromBody(body: any): string | undefined {
  if (!body) return undefined;
  if (typeof body === "string") return body.trim() || undefined;
  const errors = body.errors ?? body.Errors;
  const firstError = Array.isArray(errors)
    ? errors.find((e: unknown) => typeof e === "string" && e.trim())
    : errors && typeof errors === "object"
      ? Object.values(errors).flat().find((e) => typeof e === "string" && e.trim())
      : undefined;
  return firstError ?? body.message ?? body.Message ?? body.title ?? undefined;
}

// Message to show the user for a failed call, falling back when the error has none
export function getErrorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

// Deduplicates concurrent identical requests so only one fetch fires.
// Key: "METHOD:url:serialized-body"
const pendingRequests = new Map<string, Promise<any>>();

function requestKey(method: string, url: string, body?: unknown): string {
  return `${method}:${url}:${body !== undefined ? JSON.stringify(body) : ""}`;
}

export function apiRequest<T>(
  path: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  return apiRequestBase<T>(env.posBaseUrl, path, options);
}

export function apiRequestBase<T>(
  baseUrl: string,
  path: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  const method = options.method ?? "GET";
  const base = baseUrl.replace(/\/+$/, "");
  const endpoint = path.startsWith("/") ? path : `/${path}`;
  const url = `${base}${endpoint}`;
  const key = requestKey(method, url, options.body);

  // Return the in-flight promise for an identical concurrent request.
  const existing = pendingRequests.get(key);
  if (existing) return existing as Promise<T>;

  const promise: Promise<T> = (async () => {
    const token = await authService.getAccessToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(options.headers ?? {}),
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const profileId = localStorage.getItem("profileId");
    const userId = localStorage.getItem("userId");
    if (profileId) headers.clientbusinessprofileid = profileId;
    if (userId) headers.userid = userId;
    headers.clientid = "0";
    headers.processcall = "true";

    const response = await fetch(url, {
      method,
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined,
    });

    if (!response.ok) {
      const text = await response.text();
      let errorBody: unknown = text;
      try {
        errorBody = text ? JSON.parse(text) : null;
      } catch {
        // Not JSON: keep the raw text
      }
      throw new ApiError(
        messageFromBody(errorBody) ?? `Request failed: ${response.status}`,
        response.status,
        errorBody,
      );
    }

    const data = await response.json();
    // The backend reports failed actions as HTTP 200 with succeeded: false.
    // GET lookups are left alone: "nothing found" (e.g. no last session yet) is not an error there.
    if (method !== "GET" && (data?.succeeded === false || data?.Succeeded === false)) {
      throw new ApiError(messageFromBody(data) ?? "Request failed.", response.status, data);
    }
    return data as T;
  })();

  pendingRequests.set(key, promise);
  promise.finally(() => pendingRequests.delete(key));

  return promise;
}

export function apiGet<T>(path: string) {
  return apiRequest<T>(path, { method: "GET" });
}

export function apiPost<T>(path: string, body?: unknown) {
  return apiRequest<T>(path, { method: "POST", body });
}

export function unwrapResponse<T>(response: any): T {
  if (response?.data !== undefined) return response.data as T;
  if (response?.Data !== undefined) return response.Data as T;
  if (response?.result !== undefined) return response.result as T;
  return response as T;
}
